import { useState, useEffect } from "react";

// Generic cache store for different data types
const cache: Record<string, unknown> = {};
const fetchPromises: Record<string, Promise<unknown> | null> = {};

/**
 * Generic cached data fetcher hook
 * @param cacheKey - Unique key for caching this data
 * @param fetchFn - Function that performs the actual fetch
 * @returns Object with data, loading state, and error
 */
export const useCachedFetch = <T>(
	cacheKey: string,
	fetchFn: () => Promise<T | undefined>,
): { data: T | undefined; loading: boolean; error: string | undefined } => {
	const [data, setData] = useState<T | undefined>(cache[cacheKey] as T | undefined);
	const [loading, setLoading] = useState<boolean>(cache[cacheKey] === undefined);
	const [error, setError] = useState<string | undefined>(undefined);

	useEffect(() => {
		if (cache[cacheKey] !== undefined) {
			setData(cache[cacheKey] as T);
			setLoading(false);
			return;
		}

		const doFetch = async () => {
			setLoading(true);
			try {
				// If fetch is already in progress, wait for it
				if (fetchPromises[cacheKey] === null || fetchPromises[cacheKey] === undefined) {
					fetchPromises[cacheKey] = fetchFn();
				}

				const result = await fetchPromises[cacheKey];
				if (result !== undefined) {
					cache[cacheKey] = result;
					setData(result as T);
				} else {
					fetchPromises[cacheKey] = null; // Reset on error so it can retry
					setError(`Failed to load ${cacheKey}`);
				}
			} catch (err) {
				fetchPromises[cacheKey] = null;
				setError(`Failed to load ${cacheKey}`);
			} finally {
				setLoading(false);
			}
		};

		doFetch();
	}, [cacheKey]);

	return { data, loading, error };
};
